/* eslint-disable react/jsx-key */
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import { JSSLink } from 'components/shared/Links/JSSLink.styles';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import {
  Image,
  RichText,
  Text,
  isEditorActive,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { StHTMLDescription, StTagInfo } from './SearchTrialItem.style';
import useResultList from 'lib/hooks/coveo/useResultList';
import useNearestSite from 'lib/hooks/coveo/useNearestSite';
import useGeoLocation from 'lib/hooks/useGeoLocation';
import headlessEngine from 'lib/utils/coveo/headlessEngine';
import { setContext } from 'lib/utils/coveo/headlessContext';
import { highlightExcerpt, sortByGoodMatch } from 'lib/utils/coveoHeadless';
import { AppDispatch, RootState } from '../../store/store';
import Pagination from './Pagination';
import { useI18n } from 'next-localization';
import { fetchProfileDetailsForCR, updateSavedTrialsForMyProfile } from 'lib/utils/axiosCDPFetcher';
import CRModal from './CRModal';
import Favorite from '@mui/icons-material/Favorite';
import { hideLoader, showLoader } from 'src/features/loaderSlice';
import { getPager, setPager } from 'lib/utils/coveo/headlessPager';
import { buildInteractiveResult, Result } from '@coveo/headless';
import { useRouter } from 'next/router';
import { Url } from 'url';
import useQuerySummary from 'lib/hooks/coveo/useQuerySummary';
import { mergeTag, scrollToSectionPath } from '../../lib/utils/common';
import getAgeRange from '../../lib/utils/coveo/getAgeRange';

const KeyMapper: any = {
  Age: 'trialminage',
  Phase: 'trialphase',
  'Participants needed': 'trialenrollment',
  'Est. Completion Date': 'trialcompletiondate',
};

export const StDescriptopn = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'limit',
})<any>(({ theme, limit }) => ({
  lineHeight: '24px',
  color: theme.palette.neutral8.main,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: limit || 3,
  height: `${limit * 24}px`,
  WebkitBoxOrient: 'vertical',
}));

const getmModalCTAButtonByName = (buttons: any, name: string): any => {
  if (buttons?.length > 0) {
    const item = buttons.find((item: any) => item?.name === name);
    if (item) {
      return {
        buttonVariant: item?.fields?.buttonVariant?.value,
        value: item?.fields?.link?.value?.text,
        anchor: item?.fields?.link,
        name: item?.displayName,
      };
    } else {
      return null;
    }
  }
};

const SearchTrialItems = (props: any): JSX.Element => {
  const theme: any = useTheme();
  const isLaptopMode = useMediaQuery(theme.breakpoints.down('lg'));
  const showFullCard = useMediaQuery(theme.breakpoints.between(960, 1250));
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down(960));
  const [savedTrials, setSavedTrials] = useState([]);
  const [stepToPagination, setStepToPagination] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isShowToolTipModal, setIsShowToolTipModal] = useState(false);
  const { location } = useGeoLocation();
  const selectedTrial = useRef<any>(null);
  const { zipCode, isVirtual, dayOfBirth, distance } = useSelector(
    (state: RootState) => state.coveoResult
  );
  const isAuthenticated: boolean = useSelector(
    (state: RootState) => state.authentication.isAuthenticated
  );
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const totalResults = useQuerySummary();
  const [updatedModalDescription, setUpdatedModalDescription] = useState({ value: '' });
  //Pagination
  const MAX_PAGINATION_STEPS = props?.fields?.viewMoreCount?.value || 2;
  const AMOUNT_TO_PAGINATION =
    props?.fields?.noOfRecordsPerPage?.value * (props?.fields?.viewMoreCount?.value + 1) || 18;
  const GOOD_MATCH_DISTANCE = props?.fields?.goodMatchDistance?.value || 25;
  const noResultsText = isAuthenticated
    ? props?.fields?.noResultsLoggeninText || { value: 'No Results' }
    : props?.fields?.noResultsText || { value: 'No Results' };
  const [resultsPerPage, setResultsPerPage] = useState<number>(
    props?.fields?.noOfRecordsPerPage?.value || 6
  );

  const { state, fetchMoreResults } = useResultList(
    headlessEngine,
    zipCode,
    dayOfBirth,
    location,
    resultsPerPage
  );
  const getNearestSite = useNearestSite(state, location);
  const userProfile = useSelector((state: RootState) => state.crProfile);
  const profileDetails = userProfile?.profileData?.basicDetails;
  const lineCount = props?.fields?.lineCount?.value || 3;
  const { sitecoreContext } = useSitecoreContext();

  //TODO: need to check this logic for view more cards
  //const pager = useMemo(() => getPager(props?.fields?.noOfRecordsPerPage?.value || 6), []);
  const pager = useMemo(() => getPager(resultsPerPage), []);

  const [noResultsTextDelay, setNoResultsTextDelay] = useState<boolean>(false);

  const {
    seeIfYouQualifyButton,
    viewDetailsButton,
    modalCTAButtons,
    modalTitle,
    modalDescription,
  } = props?.fields;
  const cancel = getmModalCTAButtonByName(modalCTAButtons, 'Cancel');
  const continueBtn = getmModalCTAButtonByName(modalCTAButtons, 'Continue');
  const { t } = useI18n();
  const [fieldsData] = useState(() => {
    const fieldsData: any = [];
    props?.fields?.searchTrialFields?.forEach((item: any) => {
      if (item?.fields?.key?.value && item?.fields?.icon?.value && item?.fields?.label?.value) {
        fieldsData.push({
          label: item?.fields?.label,
          icon: item?.fields?.icon,
          key: item?.fields?.key,
        });
      }
    });
    return fieldsData;
  });

  const onCloseModal = () => {
    setOpenModal(false);
  };

  const onCancelModal = () => {
    setOpenModal(false);
  };

  const ShowTooltipDetailClickHandler = (e: any) => {
    e.preventDefault();
    setIsShowToolTipModal(isTabletOrMobile);
  };

  const handleActionCTAClick = (event: any) => {
    event?.preventDefault();
    const id = event?.target?.id;
    if (id === 'Register') {
      scrollToSectionPath('#cr-registration', 0, 500);
      setIsShowToolTipModal(false);
    } else {
      setIsShowToolTipModal(false);
    }
  };

  const ShowTooltipDetailModal = () => {
    if (isTabletOrMobile) {
      return (
        <CRModal
          open={isShowToolTipModal}
          handleClose={() => setIsShowToolTipModal(false)}
          handleActionClick={handleActionCTAClick}
          title={props?.fields?.saveModalTitle?.value}
          ctaButtons={props?.fields?.saveModalCTAButtons}
        >
          <Box sx={{ color: (theme) => theme.palette.neutral8.dark }}>
            <Text field={props?.fields?.saveModalDescription} />
          </Box>
        </CRModal>
      );
    } else {
      return <></>;
    }
  };

  const handleSaveAndUnsaveTrial = async (isSaved: boolean) => {
    const payload: any = {
      myProfileToken: sessionStorage.getItem('myProfileVerificationToken'),
      trialId: selectedTrial?.current,
      isSaved: isSaved ? true : false,
    };
    try {
      dispatch(showLoader());
      await updateSavedTrialsForMyProfile(payload);
      await getSetProfileData();
      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
    }
  };
  const onContinueModal = async () => {
    // api call to be done
    if (!selectedTrial.current) {
      return;
    }
    setOpenModal(false);
    await handleSaveAndUnsaveTrial(false);
    selectedTrial.current = null;
  };

  const getSetProfileData = async () => {
    const myProfileVerificationToken = sessionStorage.getItem('myProfileVerificationToken');
    if (myProfileVerificationToken) {
      try {
        const { data } = await fetchProfileDetailsForCR(myProfileVerificationToken);
        setSavedTrials(data?.trialIds);
      } catch (_e) {}
    }
  };

  useEffect(() => {
    dispatch(showLoader());
    setTimeout(() => {
      setNoResultsTextDelay(true);
    }, 3000);

    if (noResultsTextDelay) {
      dispatch(hideLoader());
    }
  }, [noResultsTextDelay]);

  useEffect(() => {
    const fetchMyProfile = async () => {
      if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
        return;
      }
      await getSetProfileData();
    };
    fetchMyProfile();
  }, []);

  const isGoodMatchByDistance = useCallback(
    (item: any) => {
      const nearestSite = getNearestSite(item);
      return parseFloat(nearestSite as string) <= GOOD_MATCH_DISTANCE ? true : false;
    },
    [getNearestSite]
  );

  const filteredAndSortedResults = useMemo(() => {
    if (!state?.results || !state?.results.length) {
      return [];
    }

    const featuredtrials = [...state.results].filter((item) => item.raw?.isfeaturedtrial === '1');
    const otherTrials = [...state.results].filter((item) => item.raw?.isfeaturedtrial !== '1');

    const featuredSortedByGoodMatch = featuredtrials.sort((a, b) =>
      sortByGoodMatch(a, b, isGoodMatchByDistance)
    );

    const sortedTrials = featuredtrials.length
      ? [...featuredSortedByGoodMatch, ...otherTrials]
      : [...state.results].sort((a, b) => sortByGoodMatch(a, b, isGoodMatchByDistance));

    if (isVirtual) {
      return sortedTrials.filter((item: any) => item.raw.isvirtual);
    } else {
      return sortedTrials;
    }
  }, [isGoodMatchByDistance, isVirtual, state?.results, distance]);

  const handleFetchMoreResults = () => {
    if (stepToPagination < MAX_PAGINATION_STEPS) {
      setStepToPagination((prev) => prev + 1);
    }
    fetchMoreResults();
  };

  const handleOnClick = async (data: any, isSave: boolean, title = '') => {
    const updatedText = mergeTag(modalDescription?.value, {
      TrialStudyName: title,
    });
    setUpdatedModalDescription({ value: updatedText });
    if (!isTokenAvailable()) {
      return;
    }
    selectedTrial.current = data?.trialname;
    if (isSave === false) {
      setOpenModal(true);
    } else {
      await handleSaveAndUnsaveTrial(true);
    }
  };

  const onCardLinkClick = (item: Result, isFeatured: boolean) => () => {
    const interactiveResultController = buildInteractiveResult(headlessEngine, {
      options: { result: item },
    });
    interactiveResultController.select();

    // need to send click analytics before open trial page
    setTimeout(() => {
      router.push(
        isFeatured ? (item.raw.featuredeprurl as Url) : (item.clickUri as unknown as Url)
      );
    }, 1000);
  };

  useEffect(() => {
    if (stepToPagination === MAX_PAGINATION_STEPS) {
      setResultsPerPage(AMOUNT_TO_PAGINATION);
    } else {
      setResultsPerPage(
        stepToPagination * props?.fields?.noOfRecordsPerPage?.value +
          props?.fields?.noOfRecordsPerPage?.value
      );
    }
  }, [stepToPagination]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    setPager(pager);
  }, [totalResults]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    const userInterests = userProfile?.profileData.conditions?.map(
      (item: any) => item.conditionValue
    );
    setContext(
      userProfile && {
        gender: profileDetails?.gender,
        dateOfBirth: profileDetails?.dob,
        interests: userInterests,
      }
    );
  }, [
    profileDetails?.dob,
    profileDetails?.gender,
    userProfile,
    userProfile?.profileData.conditions,
  ]);

  const isTokenAvailable = () => {
    return sessionStorage.getItem('myProfileVerificationToken') ? true : false;
  };

  useEffect(() => {
    if (!isTabletOrMobile) {
      setIsShowToolTipModal(false);
    }
  }, [isTabletOrMobile]);

  return (
    <Grid item container>
      <Grid
        item
        container
        xs={12}
        sx={{
          padding: '16px',
          boxSizing: 'border-box',
          flexWrap: 'wrap',
          alignContent: 'flex-start',
          background: isTabletOrMobile
            ? 'linear-gradient(180deg, rgba(0, 85, 135, 0.8) 3.8%, rgba(0, 142, 159, 0.8) 30.68%, rgba(0, 191, 179, 0) 72.1%)'
            : 'none',
        }}
        columnSpacing={1.5}
        rowSpacing={2}
      >
        {filteredAndSortedResults.length > 0
          ? filteredAndSortedResults.map((item: any) => {
              return (
                <>
                  <Grid
                    item
                    container
                    xs={12}
                    md={12}
                    lg={6}
                    key={item.UniqueId}
                    sx={{
                      minWidth: showFullCard ? '100%' : '330px',
                      height: 'fit-content',
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: showFullCard ? '100%' : '330px',
                        width: '100%',
                        height: 'fit-content',
                      }}
                    >
                      <Box
                        sx={{
                          padding: '24px',
                          borderRadius: '16px',
                          background: '#FFF',
                          boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
                          width: '100%',
                        }}
                      >
                        <Grid
                          item
                          container
                          sx={{
                            flexDirection: 'row',
                            marginBottom: '24px',
                          }}
                        >
                          {item.raw.trialenrollingstatus === 'Enrolling' &&
                            isGoodMatchByDistance(item) && (
                              <StTagInfo item sx={{ background: '#077D86' }}>
                                {t('goodMatch')}
                              </StTagInfo>
                            )}
                          {JSON.parse(item.raw.isvirtual || false) && (
                            <StTagInfo item sx={{ background: '#C42EA5' }}>
                              {t('virtual')}
                            </StTagInfo>
                          )}
                          {item.raw.trialenrollingstatus && (
                            <StTagInfo item sx={{ background: '#595959' }}>
                              {item.raw.trialenrollingstatus}
                            </StTagInfo>
                          )}
                          <Grid
                            item
                            flex={1}
                            textAlign={'right'}
                            alignSelf={'center'}
                            onClick={(e) =>
                              !isTokenAvailable()
                                ? ShowTooltipDetailClickHandler(e)
                                : e?.preventDefault()
                            }
                          >
                            <Tooltip
                              id={item.UniqueId}
                              title={
                                !isTokenAvailable()
                                  ? props?.fields?.saveButtonHoverText?.value
                                  : props?.fields?.saveButtonHoverTextLoggenIn?.value
                              }
                            >
                              {savedTrials &&
                              (savedTrials as any)?.indexOf(item?.raw?.trialname as string) > -1 ? (
                                <Favorite
                                  sx={{ cursor: 'pointer', color: 'black' }}
                                  onClick={() => handleOnClick(item.raw, false, item.title)}
                                />
                              ) : (
                                <FavoriteBorderIcon
                                  sx={{ cursor: 'pointer', color: 'black' }}
                                  onClick={() => handleOnClick(item.raw, true, item.title)}
                                />
                              )}
                            </Tooltip>
                          </Grid>
                        </Grid>
                        <StHTMLDescription item>
                          <StDescriptopn
                            dangerouslySetInnerHTML={{ __html: highlightExcerpt(item) }}
                            limit={lineCount}
                          />
                        </StHTMLDescription>
                        {fieldsData.map((field: any, index: number) => (
                          <Grid
                            key={index}
                            item
                            container
                            flexDirection={'row'}
                            justifyContent={'space-between'}
                            mb={3}
                          >
                            <Image field={field.icon} />
                            <Grid
                              item
                              ml={2}
                              fontWeight={600}
                              sx={{ color: (theme) => theme.palette.hintText.main }}
                            >
                              {field.label.value}
                            </Grid>
                            <Grid
                              item
                              flex={1}
                              textAlign={'right'}
                              sx={{
                                ...(isLaptopMode && { marginLeft: 2 }),
                                color: (theme) => theme.palette.common.black,
                              }}
                            >
                              {field?.key?.value === 'Nearest Site'
                                ? getNearestSite(item) || <span>&#8212;</span>
                                : field?.key?.value === 'Age'
                                ? getAgeRange(item?.raw?.trialminage, item?.raw?.trialmaxage) || (
                                    <span>&#8212;</span>
                                  )
                                : item?.raw?.[KeyMapper[field.key.value]]}
                            </Grid>
                          </Grid>
                        ))}

                        <Grid
                          item
                          container
                          spacing={1.5}
                          sx={{
                            boxSizing: 'border-box',
                          }}
                        >
                          {item.clickUri && (
                            <Grid
                              item
                              xs={item.raw.featuredeprurl ? 6 : 12}
                              lg={item.raw.featuredeprurl ? 6 : 12}
                            >
                              <JSSLink
                                sx={{ width: '100%', minWidth: 'initial', cursor: 'pointer' }}
                                id={''}
                                className={''}
                                key={''}
                                onClick={onCardLinkClick(item, false)}
                                variant="outlined"
                                field={{
                                  value: {
                                    // href: item.clickUri,
                                    text: viewDetailsButton?.value || 'View Details',
                                  },
                                }}
                                aria-label={'text'}
                              />
                            </Grid>
                          )}
                          {item.raw.featuredeprurl && (
                            <Grid item xs={6} lg={6}>
                              <JSSLink
                                sx={{ width: '100%', minWidth: 'initial', cursor: 'pointer' }}
                                id={''}
                                className={''}
                                key={''}
                                onClick={onCardLinkClick(item, true)}
                                variant="contained"
                                field={{
                                  value: {
                                    // href: item.raw.featuredeprurl,
                                    text: seeIfYouQualifyButton?.value || 'See if you qualify',
                                  },
                                }}
                                aria-label={'text'}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                </>
              );
            })
          : state?.isLoading !== undefined &&
            !state?.isLoading &&
            filteredAndSortedResults.length === 0 &&
            noResultsTextDelay && (
              <Grid
                id="no-result"
                sx={{
                  marginLeft: '1rem',
                  marginTop: '2rem',
                  fontSize: '18px',
                  marginBottom: '20px',
                  color: theme.palette.neutral8.main,
                }}
              >
                <RichText field={noResultsText} />
              </Grid>
            )}

        {state?.moreResultsAvailable &&
        stepToPagination < MAX_PAGINATION_STEPS &&
        filteredAndSortedResults.length ? (
          <Box sx={{ margin: '0 auto' }}>
            <Button onClick={handleFetchMoreResults}>
              <Text field={props?.fields?.viewMoreText}></Text>
            </Button>
          </Box>
        ) : null}

        <CRModal
          open={openModal}
          handleClose={onCloseModal}
          handleCancel={onCancelModal}
          handleContinue={onContinueModal}
          title={modalTitle?.value}
          confirmButton={continueBtn}
          cancelButton={cancel}
        >
          <RichText field={updatedModalDescription} />
        </CRModal>
        <ShowTooltipDetailModal />
      </Grid>
      {stepToPagination === MAX_PAGINATION_STEPS &&
        filteredAndSortedResults.length &&
        totalResults > AMOUNT_TO_PAGINATION && <Pagination pager={pager} />}
    </Grid>
  );
};

export default SearchTrialItems;
